'use client';

import BundleContextStrip from "src/components/bundles/BundleContextStrip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useParams, useSearchParams } from 'next/navigation';
import { useRouter } from 'src/i18n/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { Box, CircularProgress, Alert, Snackbar } from '@mui/material';
import { Header } from '@components/header';
import { useProductWithMediaDefinition } from 'src/hooks/api/useProductWithMedia';
import { useOrder } from 'src/contexts';
import { useCartDrawer } from 'src/contexts/cart-drawer-context';
import { orderApi } from 'src/services/api';
import { useQueryClient } from '@tanstack/react-query';
import { orderQueryKey } from 'src/hooks/useOrder';
import * as Sentry from '@sentry/nextjs';
import type { SlotDesign } from 'src/components/greeting-card/design-data';
import type { ProductDefinitionShape } from 'src/components/greeting-card/GreetingCardDesigner';
import { useResolvedTemplates } from 'src/hooks/api/useTemplates';
import type { CardTemplate } from 'src/components/greeting-card/templates/types';
import { useCardDraft } from 'src/components/greeting-card/editor/use-card-draft';
import { mapLegacySlotKeys, TEMPLATE_CATEGORIES } from 'src/components/greeting-card/constants';
import { slotsToPrint, withAllSides } from 'src/components/greeting-card/slot-sides';
import { pickerTemplatesForKind } from 'src/components/greeting-card/editor/template-filtering';
import { fitSlotToCanvas } from 'src/components/greeting-card/canvas-fit';
import { usePartyInvitationTemplate } from 'src/party/use-party-invitation-template';
import {
  trackCardAddToCartFailed,
  trackCardAddedToCart,
  trackEditorOpened,
  trackTemplateChosen,
} from 'src/utils/editor-funnel';

// ── Local type for the raw API response (generated API returns `object`) ──────
interface RawDefinition {
  id: string;
  kind?: string;
  name?: string | Record<string, string>;
  paperIds?: string[];
  price?: number;
  priceCurrencies?: Record<string, number>;
  minQuantity?: number;
  mediaSlots?: Array<{
    key: string;
    label?: string | Record<string, string>;
    printSpec: { widthMm: number; heightMm: number; bleedMm: number; safeMm?: number; dpi: number };
    input?: 'design' | 'upload';
    templates?: string[];
    required?: boolean;
  }>;
}

// react-konva must not run on the server — dynamic import with ssr:false is required.
const GreetingCardDesigner = dynamic(
  () => import('src/components/greeting-card/GreetingCardDesigner'),
  {
    ssr: false,
    loading: () => (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    ),
  }
);

export default function DesignPage() {
  const params = useParams<{ definitionId: string }>();
  // Landing pages / the products page can deep-link a pre-filtered template
  // gallery: ?filter=birthday (or the legacy ?category=birthday) preselects that
  // chip (invalid/absent slugs are ignored). `filter` wins when both are present.
  const searchParams = useSearchParams();
  const categoryParam = searchParams?.get('filter') ?? searchParams?.get('category');
  const initialTemplateCategory =
    categoryParam && (TEMPLATE_CATEGORIES as readonly string[]).includes(categoryParam)
      ? categoryParam
      : null;
  // ?template=<code> (e.g. from the marketing site) applies that template on
  // load, as if picked. Validated against the product's templates in the
  // editor; unknown codes are ignored there.
  const templateParam = searchParams?.get('template')?.trim() || null;
  // ?party=<id> (the printed-set screen's "Uredite sami"): the party invitation
  // template, filled with that event's QR, name and date.
  const partyParam = searchParams?.get('party')?.trim() || null;
  // ?inviteToken= (the app's "Uredite sami"; its webview has no host login): the
  // same, loaded through the public invitation-assets endpoint instead.
  const inviteTokenParam = searchParams?.get('inviteToken')?.trim() || null;
  const definitionId = params?.definitionId ?? '';

  const t = useTranslations('editor');

  const { data: rawDefinition, isLoading, error } = useProductWithMediaDefinition(definitionId);
  // The generated API types the response as `object`; cast once here.
  const definition = rawDefinition as RawDefinition | undefined;
  const locale = useLocale();

  const { restored: restoredDraft, onChange: onSlotsChange, clear: clearDraft } =
    useCardDraft(definitionId ? `card-draft:v1:${definitionId}` : null);

  // ── Per-product template resolution ──────────────────────────────────────────
  // Derive the union of template codes listed on each media slot (empty = all for kind).
  const templateCodes = Array.from(
    new Set((definition?.mediaSlots ?? []).flatMap((s) => s.templates ?? [])),
  );
  const { data: resolvedTemplates } = useResolvedTemplates(definition?.kind, templateCodes);

  const templates: CardTemplate[] = useMemo(
    () =>
      // Menus authored under the invitation kind are not invitations.
      pickerTemplatesForKind(
        definition?.kind,
        (resolvedTemplates ?? []).map((t) => ({ ...t, id: t.code })),
      ).map((t) => ({
        id: t.code,
        kind: t.kind,
        categories: t.categories ?? [],
        name: t.name,
        // Prefer the DB-authored thumbnail (signed by /template/resolve) so
        // admin-created templates show a preview. Seeded templates have no
        // thumbnailKey, so use the per-locale static file; the <img onError>
        // in the picker still falls back to the base image if a locale is missing.
        thumbnailUrl: t.thumbnailUrl ?? `/templates/thumbs/${t.code}.${locale}.png`,
        slots: t.slots,
        paperIds: t.paperIds ?? [],
        showcasePaperId: t.showcasePaperId ?? null,
      })),
    [resolvedTemplates, locale, definition?.kind],
  );

  const partyInvitation = usePartyInvitationTemplate({
    kind: definition?.kind,
    partyId: partyParam,
    inviteToken: inviteTokenParam,
    templateCode: templateParam,
    locale,
  });
  const [qrNoteDismissed, setQrNoteDismissed] = useState(false);
  const pickerTemplates: CardTemplate[] = useMemo(
    () => (partyInvitation.template ? [partyInvitation.template, ...templates] : templates),
    [partyInvitation.template, templates],
  );

  const { ensureOrderExists } = useOrder();
  const { openCartDrawer, setCheckoutFunction } = useCartDrawer();
  const queryClient = useQueryClient();
  const router = useRouter();
  // next-intl's useRouter returns a NEW object each render; keep it in a ref so
  // the checkout-registration effect below does not depend on its identity
  // (otherwise the effect re-runs every render → setState loop).
  const routerRef = useRef(router);
  routerRef.current = router;

  const [addingToCart, setAddingToCart] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);
  // Captured after a successful add-to-cart so the cart drawer's "continue to
  // checkout" button has an order to route to.
  const [checkoutTarget, setCheckoutTarget] = useState<
    { orderId: string; guestToken: string } | null
  >(null);

  // Register / unregister the cart drawer checkout function (mirrors order/new
  // and CalendarEditor). The function routes the guest to the checkout entry;
  // the photo-options step auto-skips to delivery for photo-less orders.
  useEffect(() => {
    if (!checkoutTarget) {
      setCheckoutFunction(undefined);
      return;
    }
    const { orderId, guestToken } = checkoutTarget;
    const handleContinueToCheckout = () => {
      const params = new URLSearchParams();
      params.set('guest_session_token', guestToken);
      routerRef.current.push(`/order/${orderId}/photo-options?${params.toString()}`);
    };
    setCheckoutFunction(() => handleContinueToCheckout);
    return () => {
      setCheckoutFunction(undefined);
    };
    // routerRef + setCheckoutFunction are stable; only re-run when the target changes.
  }, [checkoutTarget, setCheckoutFunction]);

  // Reconcile a restored draft against the current definition: drop any slots
  // the definition no longer has (e.g. a stale "back" from an old draft or a
  // multi-slot template), so the editor only shows real product sides and never
  // seeds a slot that would later be rejected as an unknown media slot.
  const effectiveInitialSlots = useMemo(() => {
    if (!restoredDraft) return undefined;
    const specs = new Map((definition?.mediaSlots ?? []).map((s) => [s.key, s.printSpec]));
    // Drafts saved before the slot-key migration still carry "face".
    const filtered = mapLegacySlotKeys(restoredDraft)
      .filter((s) => specs.has(s.slotKey))
      // A draft saved at another page size (an A6 invitation draft once the
      // product is A5) is scaled onto the product's page, not printed at the
      // old size — see canvas-fit.ts.
      .map((s) => fitSlotToCanvas(s, specs.get(s.slotKey)!));
    // A draft saved before the product gained a side (invitations got a back)
    // still opens with every side, the missing ones blank.
    return filtered.length > 0 ? withAllSides(filtered, definition?.mediaSlots ?? []) : undefined;
  }, [restoredDraft, definition]);

  // ── Funnel analytics ──────────────────────────────────────────────────────
  const analyticsCtx = useMemo(
    () => ({ kind: definition?.kind ?? 'unknown', definitionId: definition?.id ?? definitionId }),
    [definition?.kind, definition?.id, definitionId],
  );
  const editorOpenedRef = useRef(false);
  useEffect(() => {
    if (editorOpenedRef.current || !definition?.id) return;
    editorOpenedRef.current = true;
    trackEditorOpened({
      ...analyticsCtx,
      filter: initialTemplateCategory,
      templateParam,
      locale,
    });
  }, [definition?.id, analyticsCtx, initialTemplateCategory, templateParam, locale]);

  const handleTemplateChosen = useCallback(
    ({ code, source }: { code: string; source: 'picker' | 'link' }) =>
      trackTemplateChosen({ ...analyticsCtx, code, source }),
    [analyticsCtx],
  );

  const handleAddToCart = useCallback(
    async ({
      slots,
      selectedPaperId,
      quantity,
      lineTotal,
      templateCode,
    }: {
      slots: SlotDesign[];
      selectedPaperId?: string;
      quantity: number;
      lineTotal?: { cents: number; currency: string };
      templateCode?: string | null;
    }) => {
      setCartError(null);
      setAddingToCart(true);
      try {
        // 1. Ensure an order (guest) exists.
        const { orderId, guestToken } = await ensureOrderExists();

        // 2. Only render/submit slots this definition actually has. A restored
        //    draft or an applied template can carry slots the definition lacks
        //    (e.g. a "back"/"inside" from a double-sided template on a
        //    single-sided card). Submitting those makes the backend reject the
        //    whole line with `Unknown media slot "back"`. Required slots are
        //    always in the definition, so this can never drop a required slot.
        //    An optional side left blank (the back of a single-sided
        //    invitation) is not sent either, so the print shop never gets an
        //    empty page; every designed side is.
        const printableSlots = slotsToPrint(slots, definition?.mediaSlots ?? []);
        if (printableSlots.length === 0) {
          throw new Error(t('addToCartFailed'));
        }

        // 3. Render each slot to a print PDF via the local render route,
        //    forwarding the guest token so the route can resolve image download URLs.
        const media = await Promise.all(
          printableSlots.map(async (slot, index) => {
            // Render at the product's configured dpi (e.g. 150/600), not the
            // route's 300 default; falls through to the default only if the slot
            // spec has no dpi.
            const dpi = (definition?.mediaSlots ?? []).find(
              (ms) => ms.key === slot.slotKey,
            )?.printSpec?.dpi;
            // KNOWN GAP: these renders land in storage tagged transient and the
            // bucket lifecycle deletes them 7 days later, so a card ordered and
            // not printed within a week loses its cart thumbnail and print PDF.
            // Upload-time intent cannot fix this — the tag is baked into the
            // presigned URL by the backend (see app/api/render-card/route.ts).
            // The fix is server-side: clear the tag when the order references
            // the key, the way TemplateService does for template thumbnails.
            const res = await fetch('/api/render-card', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'x-guest-token': guestToken,
              },
              body: JSON.stringify(
                typeof dpi === 'number' && dpi > 0 ? { slot, dpi } : { slot },
              ),
            });
            if (!res.ok) {
              const detail = await res.text().catch(() => '');
              throw new Error(`Render failed for slot ${slot.slotKey}: ${detail}`);
            }
            const { renderKey, previewKey } = (await res.json()) as {
              renderKey: string;
              previewKey: string;
            };

            // 3. Build the MediaItem for this slot.
            return {
              slotKey: slot.slotKey,
              rank: index,
              designData: slot as unknown as object, // full SlotDesign — re-editable source of truth
              sourcePhotoKeys: slot.layers
                .filter((l) => l.type === 'image')
                .map((l) => (l as { sourceKey: string }).sourceKey),
              renderKey,
              previewKey,
            };
          })
        );

        // 4. Call the guest add-product-with-media endpoint.
        //    Signature: orderControllerAddProductWithMedia(id, dto, xGuestToken?, options?)
        await orderApi.orderControllerAddProductWithMedia(
          orderId,
          {
            definitionId: definition!.id,
            quantity,
            media,
            ...(selectedPaperId ? { selectedPaperId } : {}),
          },
          guestToken,
        );

        // The design now lives in the cart (re-editable there) — drop the local draft.
        clearDraft();

        // 5. Refresh the order in the query cache (mirrors CalendarEditor pattern).
        try {
          const updatedOrderResponse = await orderApi.getOneBaseOrderControllerOrder(
            orderId,
            guestToken,
            undefined,
            [
              'orderCalendars',
              'orderPhotos',
              'orderProductDetails',
              'productWithMediaOrders',
              'productWithMediaOrders.definition',
            ],
            undefined,
          );
          const updatedOrder = updatedOrderResponse.data;
          if (updatedOrder?.id) {
            queryClient.setQueryData(orderQueryKey(updatedOrder.id), updatedOrder);
          }
        } catch (fetchErr) {
          // Non-fatal: cart will refresh on next open.
          console.error('DesignPage: failed to refresh order after add-to-cart:', fetchErr);
        }

        // 6. Register the checkout target so the cart drawer shows the
        //    "continue to checkout" button, then open the drawer.
        setCheckoutTarget({ orderId, guestToken });
        openCartDrawer();
        trackCardAddedToCart({
          ...analyticsCtx,
          quantity,
          value: lineTotal?.cents ?? 0,
          currency: lineTotal?.currency ?? 'eur',
          templateCode,
        });
      } catch (err: unknown) {
        console.error('DesignPage: add-to-cart failed:', err);
        Sentry.captureException(err);
        // Prefer the backend's actual reason (NestJS returns a string, or an
        // array of validation messages) over Axios's opaque "Request failed
        // with status code 400" — otherwise the real cause is invisible.
        const apiMessage = (() => {
          const data = (err as { response?: { data?: { message?: unknown } } })
            ?.response?.data?.message;
          if (Array.isArray(data)) return data.filter(Boolean).join(', ');
          if (typeof data === 'string' && data) return data;
          return err instanceof Error ? err.message : null;
        })();
        const shown = apiMessage || t('addToCartFailed');
        setCartError(shown);
        trackCardAddToCartFailed({ ...analyticsCtx, quantity, message: shown });
      } finally {
        setAddingToCart(false);
      }
    },
    [analyticsCtx, clearDraft, definition, ensureOrderExists, openCartDrawer, queryClient, t]
  );

  if (isLoading) {
    return (
      <>
        <Header sticky={false} />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !definition) {
    return (
      <>
        <Header sticky={false} />
        <Box sx={{ p: 4 }}>
          <Alert severity="error">
            {error ? t('loadProductFailed') : t('productNotFound')}
          </Alert>
        </Box>
      </>
    );
  }

  // Adapt the API response to the shape the designer expects.
  const designerDefinition: ProductDefinitionShape = {
    id: definition.id,
    kind: definition.kind ?? 'greeting-card',
    name: definition.name,
    mediaSlots: (definition.mediaSlots ?? []).map((s) => ({
      key: s.key,
      label: s.label,
      printSpec: s.printSpec,
      input: s.input,
      required: s.required,
      templates: s.templates,
    })),
    paperIds: definition.paperIds ?? [],
    price: definition.price,
    priceCurrencies: definition.priceCurrencies,
    minQuantity: definition.minQuantity,
  };

  return (
    <>
      <Header sticky={false} />
      {/* The editor is full-bleed, so the box gets the page gutter. */}
      <BundleContextStrip sx={{ mx: 2, mt: 1.5, mb: 1 }} />
      {/* Inline, not a pop-up; dismissible because it takes height from the editor below. */}
      {partyInvitation.qrUnavailable && !qrNoteDismissed && (
        <Alert severity="info" onClose={() => setQrNoteDismissed(true)} sx={{ mx: 2, mt: 1, mb: 1 }}>
          {t('partyQrUnavailable')}
        </Alert>
      )}
      {/* Floating, not inline: an inline Alert is outside the editor's
          `calc(100dvh - 56px)` budget, so it shrank the canvas whenever it
          appeared. */}
      <Snackbar
        open={!!cartError}
        onClose={() => setCartError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setCartError(null)} variant="filled">
          {cartError}
        </Alert>
      </Snackbar>
      {/* The editor shell fills the viewport below the header — no width container. */}
      <GreetingCardDesigner
        definition={designerDefinition}
        templates={pickerTemplates.length > 0 ? pickerTemplates : undefined}
        initialTemplateCategory={initialTemplateCategory}
        // Held back while the event's QR loads, so the template is applied once, filled.
        initialTemplateCode={partyInvitation.pending ? null : templateParam}
        onTemplateChosen={handleTemplateChosen}
        initialSlots={effectiveInitialSlots}
        onSlotsChange={onSlotsChange}
        onAddToCart={handleAddToCart}
        addingToCart={addingToCart}
      />
    </>
  );
}
